import React from 'react'
import ReactDOM from 'react-dom'

// dayjs plugins must be registered before any feature module imports the
// singleton — otherwise per-feature `dayjs.extend(...)` calls race the
// frozen-namespace ESM bug and leave the prototype only half-patched.
import './dayjs-setup'

// Sentry must be initialized before anything else
import './sentry'

// Import Chart.js setup
import './utils/chartjs-setup'

// the order of import yup locale config is matter
// must import it before ./App in order to work properly
import './yupLocaleConfig'

import i18next from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'

import { BrowserRouter } from 'react-router-dom'
import { Provider as ReactReduxProvider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { Elements as ReactStripeElementsProvider } from '@stripe/react-stripe-js'
import { loadStripe, StripeElementLocale } from '@stripe/stripe-js'
import { Loader } from '@googlemaps/js-api-loader'

import App from './App'
import configureAppStore from './redux/store'
import MuiThemeProvider from './providers/MuiThemeProvider'
import locales from './assets/locales'
import reportWebVitals from './reportWebVitals'

import { OfflineAuthProvider } from '@/providers/OfflineAuthProvider'
import { UnleashProvider } from '@/providers/UnleashProvider'

import 'allotment/dist/style.css'
import './index.scss'
import { getEnv } from '@/utils/helpers'
import { LanguageProvider } from './providers/LanguageProvider'
import { activateLingui, defaultLanguage } from './i18n'
import { i18nextFallbackLanguages } from './i18next-config'


i18next
  .use(initReactI18next) // passes i18n down to react-i18next
  .use(LanguageDetector)
  .init({
    debug: import.meta.env.NODE_ENV === 'development' ? true : false,
    detection: {
      order: ['querystring', 'navigator', 'cookie', 'localStorage', 'sessionStorage', 'htmlTag', 'path', 'subdomain'],
    },
    resources: locales,
    // English locales may have region-specific catalog gaps, but must never
    // fall through to French. Let en and en-US fill gaps for one another.
    fallbackLng: i18nextFallbackLanguages,
    ns: ['app', 'common', 'yup', 'moment'],
    defaultNS: 'app',
    saveMissing: true,
    // Never surface a raw translation-key path (e.g. "page.planning.shift.admin.create.success")
    // to the user when a key is missing — humanize the last segment as a last-resort fallback
    // and log it so the key can be added to the catalogs.
    // i18next passes the inline defaultValue (`t(key, 'Fallback')`) as the second
    // argument; it must win over the humanized key tail, otherwise every call-site
    // default in the app gets clobbered (e.g. 'Start Date' rendered as 'Label').
    parseMissingKeyHandler: (key: string, defaultValue?: string) => {
      if (import.meta.env.NODE_ENV !== 'production') console.warn(`[i18n] Missing translation key: ${key}`)
      if (defaultValue !== undefined && defaultValue !== key) return defaultValue
      const lastSegment = key.split('.').pop() ?? key
      return lastSegment
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/^./, (char) => char.toUpperCase())
    },
    interpolation: {
      escapeValue: false,
    },
    simplifyPluralSuffix: true,
    compatibilityJSON: 'v3',
  })

void activateLingui(defaultLanguage)

const { store, persistor } = configureAppStore()

// if (typeof global === "undefined") {
//   window.global = window;
// }

// Make sure to call `loadStripe` outside of a component's render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(getEnv('VITE_STRIPE_PK')!)

const googleMapsLoader = new Loader({
  apiKey: getEnv('VITE_GOOGLE_MAPS_API_KEY')!,
  libraries: ['drawing', 'geometry', 'places'],
})
googleMapsLoader.loadCallback((e) => {
  if (e) {
    console.log(e)
  }
})

ReactDOM.render(
  <React.StrictMode>
    <BrowserRouter>
      <MuiThemeProvider>
        <ReactReduxProvider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <UnleashProvider>
              <LanguageProvider>
                <ReactStripeElementsProvider stripe={stripePromise} options={{ locale: i18next.language as StripeElementLocale }}>
                  <OfflineAuthProvider>
                    <App />
                  </OfflineAuthProvider>
                </ReactStripeElementsProvider>
              </LanguageProvider>
            </UnleashProvider>
          </PersistGate>
        </ReactReduxProvider>
      </MuiThemeProvider>
    </BrowserRouter>
  </React.StrictMode>,
  document.getElementById('root')
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
